import { useMemo } from 'react'
import { CheckCheck } from 'lucide-react'
import { CanonicalLoad } from '@numeo/types-core'
import { useViewedBoxTruckLoads } from '../hooks/useViewedBoxTruckLoads'
import { loadKey } from '../utils/loadFields'

/**
 * "Mark all read" for the box-truck board — clears the undimmed state off every row currently on it.
 *
 * Scoped to the loads ON SCREEN, which is the only set the client knows: the board is a live feed the
 * operator pages through, so "all" can only mean what has been fetched. The count in the label says so
 * plainly rather than implying it swept the whole feed.
 *
 * Hidden once nothing is unread — the board is the answer at that point, and a permanently disabled
 * control in a toolbar this dense is noise.
 */
export const BoxTruckMarkAllRead = ({ loads }: { loads: CanonicalLoad[] }) => {
    const { viewedKeys, markAllViewed } = useViewedBoxTruckLoads()
    const unreadKeys = useMemo(() => loads.map(loadKey).filter((key) => !viewedKeys.has(key)), [loads, viewedKeys])
    if (unreadKeys.length === 0) return null
    return (
        <button
            type="button"
            onClick={() => markAllViewed(unreadKeys)}
            title="Dim every load currently on the board, as if you had opened them"
            className="flex h-7 items-center gap-1.5 rounded-lg px-2 text-xs font-medium text-foreground-muted transition-colors hover:bg-default hover:text-foreground"
        >
            <CheckCheck size={14} />
            Mark all read ({unreadKeys.length})
        </button>
    )
}
